import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { useBranding } from '../contexts/BrandingContext';
import { useAuthPageBackground, AUTH_BG_CLASSES } from '../hooks/useAuthPageBackground';
import { getSafeRedirectTarget } from '../lib/authRedirect';

interface LoginFormData {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login: authLogin, isAuthenticated, user } = useAuth();
  const { loadSettingsFromBackend, supplierDefaultLandingPath, isLoading: settingsLoading } = useMenuSettings();
  const { branding, isLoading: brandingLoading } = useBranding();
  const authBackground = useAuthPageBackground();
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  });
  const [rememberMe, setRememberMe] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  // Redirect if already authenticated. Pre-approval suppliers are forced to
  // /onboarding/my-progress; approved suppliers land on the platform setting;
  // logistics has its own dedicated page; everyone else goes to /dashboard.
  // We wait for menu settings to load before redirecting so the configured
  // supplier landing path is applied instead of the default fallback.
  useEffect(() => {
    if (!isAuthenticated) return;
    // For suppliers, wait until platform settings resolve so the configured
    // landing path applies on first paint instead of the default /dashboard.
    if (user?.company_type === 'supplier' && settingsLoading) return;
    let dest = '/dashboard';
    if (user?.company_type === 'logistics') {
      dest = '/logistics/call-offs';
    } else if (user?.company_type === 'supplier') {
      dest = user?.supplier_is_approved === false
        ? '/onboarding/my-progress'
        : (supplierDefaultLandingPath || '/dashboard');
    }
    // CR-5887: if the user was bounced here from a deep-link (e.g. a Daily
    // Quality Summary email link), send them back to it after login instead
    // of the role default. Pre-approval suppliers stay confined to onboarding
    // (ProtectedRoute re-gates them anyway).
    const target = getSafeRedirectTarget(location.state);
    const isPreApprovalSupplier =
      user?.company_type === 'supplier' && user?.supplier_is_approved === false;
    if (target && !isPreApprovalSupplier) {
      dest = target;
    }
    navigate(dest, { replace: true });
  }, [isAuthenticated, navigate, user, supplierDefaultLandingPath, settingsLoading, location.state]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await authLogin(formData.email, formData.password, rememberMe);
      // CR-5275: MFA branches. authLogin returns 'mfa_setup_required' /
      // 'mfa_required' without storing credentials; route to the matching
      // step UI carrying the session token. 'success' falls through and
      // the existing isAuthenticated useEffect redirects to dashboard.
      // CR-5887: carry any captured deep-link target through the MFA step so
      // an email link still resolves after the second factor.
      const from = (location.state as { from?: unknown } | null)?.from;
      if (result.kind === 'mfa_setup_required') {
        navigate('/mfa-setup', { state: { sessionToken: result.sessionToken, from } });
        return;
      }
      if (result.kind === 'mfa_required') {
        navigate('/mfa-verify', { state: { sessionToken: result.sessionToken, from } });
        return;
      }
      // Load menu settings from backend after successful login (non-blocking)
      loadSettingsFromBackend().catch(() => {
        // Silently handle - defaults will be used
      });
      // Navigation is handled by the useEffect watching isAuthenticated
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { status?: number; data?: Record<string, string[]|string> };
        request?: unknown;
      };
      if (axiosErr.response?.data) {
        // Handle specific error messages from API
        const errorData = axiosErr.response.data;
        // Sign-in is rate limited. The throttle answers 429 with the API's
        // {status, code, message} envelope and none of the field keys below, so
        // it used to fall through to "Invalid email or password" — telling
        // someone whose password is perfectly correct that it is wrong. They
        // then retry, which extends the very lockout they are being told
        // nothing about, or reset a password that was never the problem.
        if (axiosErr.response.status === 429) {
          const seconds = Number(String(errorData['message'] ?? '').match(/(\d+)\s*seconds?/)?.[1]);
          const wait = Number.isFinite(seconds) && seconds > 0
            ? ` Try again in about ${Math.max(1, Math.ceil(seconds / 60))} minute${seconds >= 120 ? 's' : ''}.`
            : ' Please wait a few minutes and try again.';
          setError(`Too many sign-in attempts.${wait}`);
        } else if (errorData['non_field_errors']) {
          const msgs = errorData['non_field_errors'];
          setError(Array.isArray(msgs) ? msgs[0] : String(msgs));
        } else if (errorData['error']) {
          setError(String(errorData['error']));
        } else if (errorData['email']) {
          const msgs = errorData['email'];
          setError(`Email: ${Array.isArray(msgs) ? msgs[0] : String(msgs)}`);
        } else if (errorData['password']) {
          const msgs = errorData['password'];
          setError(`Password: ${Array.isArray(msgs) ? msgs[0] : String(msgs)}`);
        } else {
          setError('Invalid email or password. Please try again.');
        }
      } else if (axiosErr.request) {
        setError('Unable to connect to the server. Please check your connection.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading spinner while branding loads
  if (brandingLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    // CR-6243: shared with Forgot Password and Reset Password — see
    // useAuthPageBackground.
    <div
      className={`min-h-screen flex items-center justify-center ${AUTH_BG_CLASSES}`}
      style={authBackground}
    >
      <div className="max-w-md w-full mx-4">
        {/* Login Card */}
        <div className="bg-white rounded-lg shadow-xl p-8">
          {/* Logo/Header */}
          <div className="text-center mb-8">
            {/* CR-6243: deliberately the LIGHT logo regardless of theme — the
                card below is `bg-white` with no dark variant, so a dark logo
                would sit dark-on-white. If this card is ever made
                theme-aware, switch to useThemedLogo().signInLogo. */}
            {branding.companyLogo ? (
              <img
                src={branding.companyLogo}
                alt={branding.companyName}
                className="mx-auto mb-4 max-h-20 max-w-[200px] object-contain"
              />
            ) : (
              <div
                className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
                style={{ backgroundColor: branding.loginColor }}
              >
                <svg
                  className="w-10 h-10 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
            )}
            {(branding.loginTitle?.trim() || !branding.companyLogo) && (
              <h1 className="text-3xl font-bold text-gray-900">
                {branding.loginTitle?.trim() || branding.companyName || 'Supplier Portal'}
              </h1>
            )}
            <p className="text-gray-600 mt-2">Sign in to your account</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <svg
                  className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                placeholder="you@company.com"
                disabled={loading}
              />
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="current-password"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                placeholder="Enter your password"
                disabled={loading}
              />
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="remember-me"
                  className="ml-2 block text-sm text-gray-700"
                >
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <Link
                  to="/forgot-password"
                  className="font-medium text-indigo-600 hover:text-indigo-500"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            {/* Terms Agreement */}
            <p className="text-xs text-gray-500 text-center">
              By signing in, you agree to our{' '}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:text-indigo-500 underline"
              >
                Terms & Conditions
              </a>{' '}
              and{' '}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:text-indigo-500 underline"
              >
                Privacy Policy
              </a>
            </p>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed transition-colors hover:opacity-90"
              style={{
                backgroundColor: branding.buttonColor,
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          {/* Footer */}
          {/* Removed "Contact your administrator" — not relevant for this portal */}
        </div>

        {/* Powered-by footer */}
        <div className="mt-8 flex items-center justify-center gap-2 text-sm text-gray-500">
          <span>Powered by</span>
          <img src="/valuechain-logo.png" alt="Valuechain" className="h-14 w-auto" />
        </div>
      </div>
    </div>
  );
};

export default Login;
